/**
 * AiTelemetrySection — Wave 1 debt closeout.
 *
 * Read-only operational telemetry for the family assistant, mounted inside
 * <AdminDashboardPanel> beneath the AI-cost section. Surfaces the previously-
 * dead ai.* bus events (messages flushed, tool-call tallies, allergy-filter
 * corrections, assistant-driven shopping adds), accumulated since the API
 * booted. Complementary to the DB-backed cost panel: this is health, not bill.
 *
 * State-primitive contract (mirrors PushHealthTable):
 *   - Loading  → <LoadingState lines={3} />
 *   - Error    → <ErrorState variant="inline" onRetry=refetch />
 *   - Empty    → "Ingen aktivitet ennå" (no events since boot)
 *   - Populated → stat lines + per-tool + per-allergen tallies
 *
 * Numbers use the JetBrains-Mono `.tnum` tabular class for alignment, matching
 * the AssistantAdminPanel cost rows.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, LoadingState } from '../../components/primitives';
import { useAiTelemetry } from './useAiTelemetry';

/** nb-NO short date+time for the "since boot" line. */
const fmtSince = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function StatRow({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-2">{label}</span>
      <span className="tnum font-bold text-ink">{value}</span>
    </div>
  );
}

/** Tallflis som i Stell_Admin: inset, eller warn for allergirettingene. */
function Tile({ label, value, warn }: { label: string; value: number; warn?: boolean }): ReactElement {
  return (
    <div className={`flex flex-col items-center rounded-md px-1 py-2 ${warn ? 'bg-warn-bg text-warn' : 'bg-inset text-ink'}`}>
      <span className="tnum text-base font-extrabold">{value}</span>
      <span className={`text-center text-label font-bold ${warn ? 'text-warn' : 'text-ink-2'}`}>{label}</span>
    </div>
  );
}

export function AiTelemetrySection(): ReactElement {
  const { t } = useTranslation();
  const query = useAiTelemetry();

  if (query.isLoading) {
    return <LoadingState lines={3} />;
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        variant="inline"
        heading={t('admin.ai_telemetry.load_error')}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const d = query.data;
  const isEmpty =
    d.messages_flushed === 0 &&
    d.shopping_added === 0 &&
    d.corrections === 0 &&
    d.briefings_generated === 0 &&
    d.memory_suggested === 0 &&
    d.tools.length === 0;

  let sinceLabel: string;
  try {
    sinceLabel = fmtSince.format(new Date(d.since));
  } catch {
    sinceLabel = d.since;
  }

  return (
    <div data-testid="ai-telemetry-section" className="space-y-3">
      <p className="text-xs text-ink-2">{t('admin.ai_telemetry.since', { time: sinceLabel })}</p>

      {isEmpty ? (
        <p className="text-sm text-ink-2">{t('admin.ai_telemetry.none')}</p>
      ) : (
        <>
          {/* Flisene fra skissens «AI i dag». Tallene teller siden oppstart,
              ikke i dag (#159), så de står her under «Siden …». */}
          <div className="grid grid-cols-3 gap-1.5">
            <Tile label={t('admin.ai_telemetry.messages_flushed')} value={d.messages_flushed} />
            <Tile label={t('admin.ai_telemetry.corrections')} value={d.corrections} warn />
            <Tile label={t('admin.ai_telemetry.briefings_generated')} value={d.briefings_generated} />
          </div>
          <div className="space-y-1">
            <StatRow label={t('admin.ai_telemetry.shopping_added')} value={d.shopping_added} />
            <StatRow label={t('admin.ai_telemetry.briefings_corrected')} value={d.briefings_corrected} />
            {/* Nattjobben med notatforslag (#196); forbruket står i ai_turn_log som 'memory'. */}
            <StatRow label={t('admin.ai_telemetry.memory_suggested')} value={d.memory_suggested} />
          </div>

          {d.tools.length > 0 && (
            <div className="space-y-1">
              <h4 className="m-0 text-caption font-bold text-ink-2">
                {t('admin.ai_telemetry.tools')}
              </h4>
              <ul className="space-y-1">
                {d.tools.map((tool) => (
                  <li key={tool.tool} className="text-xs text-ink-2 tnum">
                    {t('admin.ai_telemetry.tool_row', {
                      tool: tool.tool,
                      count: tool.count,
                      avg: tool.avg_ms,
                      max: tool.max_ms,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {d.corrected_allergens.length > 0 && (
            <ul className="space-y-1">
              {d.corrected_allergens.map((a) => (
                <li key={a.allergen} className="text-xs text-ink-2 tnum">
                  {t('admin.ai_telemetry.allergen_row', { allergen: a.allergen, count: a.count })}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
